export const getUbiquitiDataArray = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (response?.data && typeof response.data === "object") {
    return getUbiquitiDataArray(response.data);
  }

  for (const key of ["devices", "clients", "wans", "broadcasts", "activity", "activities", "periods", "metrics", "access_points", "accessPoints", "floor_plans", "floorPlans", "items", "results"]) {
    if (Array.isArray(response?.[key])) {
      return response[key];
    }
  }

  return [];
};

const getUbiquitiSiteId = (site) => (
  site?.siteId
  || site?.id
  || site?._id
  || site?.uuid
  || site?.meta?.siteId
  || site?.meta?.id
  || site?.meta?.name
  || site?.name
  || ""
);

const normalizeUbiquitiIdentifier = (value) => String(value || "").trim().toLowerCase();

const getUniqueUbiquitiIds = (values) => [...new Set(
  values
    .flatMap((value) => {
      const normalizedValue = normalizeUbiquitiIdentifier(value);

      return normalizedValue ? [normalizedValue, normalizedValue.split(":")[0]] : [];
    })
    .filter(Boolean),
)];

const normalizeUbiquitiKey = (key) => String(key || "").replace(/[\\_\-\s]/g, "").toLowerCase();

const getUbiquitiObjectValue = (source, key) => {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const normalizedKey = normalizeUbiquitiKey(key);
  const matchingKey = Object.keys(source).find((sourceKey) => normalizeUbiquitiKey(sourceKey) === normalizedKey);

  return matchingKey ? source[matchingKey] : undefined;
};

const pickUbiquitiValue = (source, keys) => {
  for (const key of keys) {
    const value = key.includes(".")
      ? key.split(".").reduce((currentValue, pathKey) => getUbiquitiObjectValue(currentValue, pathKey), source)
      : getUbiquitiObjectValue(source, key);

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return null;
};

const getMetricTimestamp = (metric) => (
  metric?.metricTime
  || metric?.timestamp
  || metric?.time
  || metric?.createdAt
  || metric?.periodStart
  || metric?.start
  || metric?.datetime
  || null
);

const normalizeMetricTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = typeof value === "number" && value < 1000000000000 ? value * 1000 : value;
  const time = new Date(timestamp);

  return Number.isNaN(time.getTime()) ? null : time;
};

const formatMetricAxisLabel = (value) => {
  const time = normalizeMetricTimestamp(value);

  if (!time) {
    return "";
  }

  return time.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
};

const formatMetricTooltipLabel = (value) => {
  const time = normalizeMetricTimestamp(value);

  if (!time) {
    return "";
  }

  return time.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getUbiquitiAxisLabelIndexes = (totalRows, labelCount = 7) => {
  if (totalRows <= 0) {
    return new Set();
  }

  if (totalRows <= labelCount) {
    return new Set(Array.from({ length: totalRows }, (_, index) => index));
  }

  return new Set(Array.from({ length: labelCount }, (_, index) => Math.round((index * (totalRows - 1)) / (labelCount - 1))));
};

const ISP_METRIC_24H_MS = 24 * 60 * 60 * 1000;
const ISP_METRIC_24H_BUCKETS = 288;

const getIspMetricContainers = (value, depth = 0) => {
  if (!value || depth > 4) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => getIspMetricContainers(item, depth + 1));
  }

  if (typeof value !== "object") {
    return [];
  }

  if (Array.isArray(value.periods) || getMetricTimestamp(value)) {
    return [value];
  }

  return ["data", "items", "results", "metrics"]
    .flatMap((key) => getIspMetricContainers(value[key], depth + 1));
};

const getVisibleIspMetricRows = (rows) => {
  const latestTimestamp = rows.reduce((latest, metric) => {
    const timestamp = normalizeMetricTimestamp(getMetricTimestamp(metric))?.getTime() || 0;

    return Math.max(latest, timestamp);
  }, 0);
  const rangeStart = latestTimestamp - ISP_METRIC_24H_MS;
  const filteredRows = latestTimestamp
    ? rows.filter((metric) => (normalizeMetricTimestamp(getMetricTimestamp(metric))?.getTime() || 0) >= rangeStart)
    : rows;

  return filteredRows.slice(-ISP_METRIC_24H_BUCKETS);
};

const getIspMetricSourceSiteId = (source) => source?.siteId || source?.site_id || "";

const getIspMetricSourceHostId = (source) => source?.hostId || source?.host_id || "";

const getIspMetricSourceId = (source, index) => (
  String(source?.__ubiquitiMetricSourceId
  || getIspMetricSourceHostId(source)
  || getIspMetricSourceSiteId(source)
  || `source-${index}`)
);

const getIndexedIspMetricSources = (ispMetrics) => getIspMetricContainers(ispMetrics)
  .map((source, index) => ({
    ...source,
    __ubiquitiMetricSourceId: getIspMetricSourceId(source, index),
    __ubiquitiMetricSourceIndex: index,
  }));

const doesMetricIdMatch = (sourceId, expectedIds) => {
  const normalizedSourceId = normalizeUbiquitiIdentifier(sourceId);
  const sourceParts = getUniqueUbiquitiIds([normalizedSourceId]);

  return sourceParts.some((sourcePart) => expectedIds.some((expectedId) => (
    sourcePart === expectedId
    || sourcePart.includes(expectedId)
    || expectedId.includes(sourcePart)
  )));
};

const getIspMetricSourcePeriods = (source) => {
  const sourcePeriods = Array.isArray(source?.periods) ? source.periods : [source];
  const sourceHostId = getIspMetricSourceHostId(source);
  const sourceSiteId = getIspMetricSourceSiteId(source);

  return sourcePeriods
    .filter((period) => period && typeof period === "object")
    .map((period) => ({
      ...period,
      hostId: period.hostId || period.host_id || sourceHostId,
      siteId: period.siteId || period.site_id || sourceSiteId,
    }));
};

const getIspMetricSourceThroughputScore = (source) => getIspMetricSourcePeriods(source)
  .reduce((score, period) => {
    const download = getIspMetricNumber(period, "download_kbps") || 0;
    const upload = getIspMetricNumber(period, "upload_kbps") || 0;

    return score + Math.max(download, 0) + Math.max(upload, 0);
  }, 0);

const getSelectedIspMetricSources = (ispMetrics, siteId = "", hostIds = [], selectedSourceId = "") => {
  const sources = getIndexedIspMetricSources(ispMetrics);
  const normalizedSelectedSourceId = normalizeUbiquitiIdentifier(selectedSourceId);
  const normalizedHostIds = getUniqueUbiquitiIds(hostIds);
  const normalizedSiteIds = getUniqueUbiquitiIds([siteId]);
  const selectedSources = normalizedSelectedSourceId
    ? sources.filter((source) => normalizeUbiquitiIdentifier(getIspMetricSourceId(source, source.__ubiquitiMetricSourceIndex)) === normalizedSelectedSourceId)
    : [];
  const hostMatchedSources = normalizedHostIds.length
    ? sources.filter((source) => doesMetricIdMatch(getIspMetricSourceHostId(source), normalizedHostIds))
    : [];
  const hostMatchedSourcesWithThroughput = hostMatchedSources.filter((source) => getIspMetricSourceThroughputScore(source) > 0);
  const siteMatchedSources = normalizedSiteIds.length
    ? sources.filter((source) => doesMetricIdMatch(getIspMetricSourceSiteId(source), normalizedSiteIds))
    : [];
  const siteMatchedSourcesWithThroughput = siteMatchedSources.filter((source) => getIspMetricSourceThroughputScore(source) > 0);
  const firstNonZeroSource = sources.find((source) => getIspMetricSourceThroughputScore(source) > 0);

  if (selectedSources.length) {
    return selectedSources;
  }

  if (hostMatchedSourcesWithThroughput.length) {
    return hostMatchedSourcesWithThroughput;
  }

  if (siteMatchedSourcesWithThroughput.length) {
    return siteMatchedSourcesWithThroughput;
  }

  if (firstNonZeroSource) {
    return [firstNonZeroSource];
  }

  return siteMatchedSources.length ? siteMatchedSources : sources;
};

const getIspMetricPeriods = (sources) => {
  const periods = sources.flatMap(getIspMetricSourcePeriods);
  const uniquePeriods = new Map();

  periods.forEach((period) => {
    const normalizedTimestamp = normalizeMetricTimestamp(getMetricTimestamp(period));

    if (!normalizedTimestamp) {
      return;
    }

    uniquePeriods.set(`${period.hostId || ""}:${period.siteId || ""}:${normalizedTimestamp.getTime()}`, period);
  });

  return [...uniquePeriods.entries()]
    .sort(([, firstPeriod], [, secondPeriod]) => {
      const firstTimestamp = normalizeMetricTimestamp(getMetricTimestamp(firstPeriod))?.getTime() || 0;
      const secondTimestamp = normalizeMetricTimestamp(getMetricTimestamp(secondPeriod))?.getTime() || 0;

      return firstTimestamp - secondTimestamp;
    })
    .map(([, period]) => period);
};

const toMetricNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().replace(/\s/g, "").replace(",", ".");
    const numericStringValue = Number(normalizedValue);

    return Number.isFinite(numericStringValue) ? numericStringValue : null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const hasExactIspMetricKeys = (value) => (
  value
  && typeof value === "object"
  && !Array.isArray(value)
  && (
    Object.prototype.hasOwnProperty.call(value, "download_kbps")
    || Object.prototype.hasOwnProperty.call(value, "upload_kbps")
    || Object.prototype.hasOwnProperty.call(value, "avgLatency")
    || Object.prototype.hasOwnProperty.call(value, "maxLatency")
    || Object.prototype.hasOwnProperty.call(value, "packetLoss")
  )
);

const getIspMetricExactValue = (metric, key) => {
  if (Object.prototype.hasOwnProperty.call(metric?.data?.wan || {}, key)) {
    return metric.data.wan[key];
  }

  if (Object.prototype.hasOwnProperty.call(metric || {}, key)) {
    return metric[key];
  }

  const directMetricObject = Object.values(metric || {}).find((value) => (
    hasExactIspMetricKeys(value)
    && Object.prototype.hasOwnProperty.call(value, key)
  ));

  return directMetricObject?.[key];
};

const getIspMetricNumber = (metric, key) => toMetricNumber(getIspMetricExactValue(metric, key));

const getIspMetricMbps = (metric, key) => {
  const numericValue = getIspMetricNumber(metric, key);

  return numericValue !== null ? numericValue / 1000 : null;
};

export const getUbiquitiIspMetricSourceOptions = (ispMetrics) => getIndexedIspMetricSources(ispMetrics)
  .filter((source) => Array.isArray(source?.periods) && source.periods.length > 0)
  .map((source, index) => {
    const periods = getIspMetricSourcePeriods(source);
    const firstPeriod = periods.find((period) => hasExactIspMetricKeys(period?.data?.wan) || hasExactIspMetricKeys(period));
    const lastPeriod = periods[periods.length - 1];
    const ispName = getIspMetricExactValue(firstPeriod, "ispName") || "WAN";
    const throughputScore = getIspMetricSourceThroughputScore(source);

    return {
      id: getIspMetricSourceId(source, index),
      label: `${ispName} (WAN1)`,
      ispName,
      siteId: getIspMetricSourceSiteId(source),
      hostId: getIspMetricSourceHostId(source),
      periods: periods.length,
      firstTime: getMetricTimestamp(firstPeriod),
      lastTime: getMetricTimestamp(lastPeriod),
      hasThroughput: throughputScore > 0,
      throughputScore,
    };
  });

export const getPreferredUbiquitiIspMetricSourceId = (ispMetrics, siteId = "", hostIds = [], selectedSourceId = "") => {
  const options = getUbiquitiIspMetricSourceOptions(ispMetrics);

  if (!options.length) {
    return "";
  }

  if (selectedSourceId && options.some((option) => option.id === selectedSourceId)) {
    return selectedSourceId;
  }

  const selectedSource = getSelectedIspMetricSources(ispMetrics, siteId, hostIds)[0];

  return selectedSource
    ? getIspMetricSourceId(selectedSource, selectedSource.__ubiquitiMetricSourceIndex)
    : options[0].id;
};

const getMetricSeriesExtreme = (rows, key, type) => {
  const values = rows
    .map((row) => row[key])
    .filter((value) => Number.isFinite(value));

  if (!values.length) {
    return null;
  }

  const extreme = type === "min" ? Math.min(...values) : Math.max(...values);

  return extreme;
};

const sumMetricValues = (values) => {
  const finiteValues = values.filter(Number.isFinite);

  return finiteValues.length ? finiteValues.reduce((sum, value) => sum + value, 0) : null;
};

const averageMetricValues = (values) => {
  const finiteValues = values.filter(Number.isFinite);

  return finiteValues.length
    ? finiteValues.reduce((sum, value) => sum + value, 0) / finiteValues.length
    : null;
};

const maxMetricValues = (values) => {
  const finiteValues = values.filter(Number.isFinite);

  return finiteValues.length ? Math.max(...finiteValues) : null;
};

const getAggregatedIspMetricRows = (rows) => {
  const rowsByTimestamp = rows.reduce((accumulator, row) => {
    const timestamp = normalizeMetricTimestamp(getMetricTimestamp(row))?.getTime();

    if (!timestamp) {
      return accumulator;
    }

    accumulator.set(timestamp, [...(accumulator.get(timestamp) || []), row]);

    return accumulator;
  }, new Map());

  return [...rowsByTimestamp.entries()]
    .sort(([firstTimestamp], [secondTimestamp]) => firstTimestamp - secondTimestamp)
    .map(([timestamp, groupedRows]) => ({
      metricTime: new Date(timestamp).toISOString(),
      data: {
        wan: {
          download_kbps: sumMetricValues(groupedRows.map((row) => getIspMetricNumber(row, "download_kbps"))),
          upload_kbps: sumMetricValues(groupedRows.map((row) => getIspMetricNumber(row, "upload_kbps"))),
          avgLatency: averageMetricValues(groupedRows.map((row) => getIspMetricNumber(row, "avgLatency"))),
          maxLatency: maxMetricValues(groupedRows.map((row) => getIspMetricNumber(row, "maxLatency"))),
          packetLoss: maxMetricValues(groupedRows.map((row) => getIspMetricNumber(row, "packetLoss"))),
        },
      },
    }));
};

const buildUbiquitiIspInternetSeries = (ispMetrics, siteId, hostIds = [], selectedSourceId = "") => {
  const selectedSources = getSelectedIspMetricSources(ispMetrics, siteId, hostIds, selectedSourceId);
  const metricRows = getIspMetricPeriods(selectedSources);
  const visibleRows = getVisibleIspMetricRows(getAggregatedIspMetricRows(metricRows));
  const step = Math.max(1, Math.ceil(visibleRows.length / 6));
  const rows = visibleRows.map((metric, index) => {
    const timestamp = getMetricTimestamp(metric);
    const axisLabel = formatMetricAxisLabel(timestamp);
    const tooltipLabel = formatMetricTooltipLabel(timestamp);
    const isLastPoint = index === visibleRows.length - 1;
    const isTickPoint = index % step === 0;

    return {
      label: isLastPoint ? "Now" : isTickPoint ? axisLabel : "",
      tooltipLabel: isLastPoint && tooltipLabel ? `${tooltipLabel} - Now` : tooltipLabel,
      usesIspMetrics: true,
      avgDownload: getIspMetricMbps(metric, "download_kbps"),
      avgUpload: getIspMetricMbps(metric, "upload_kbps"),
      avgLatency: getIspMetricNumber(metric, "avgLatency"),
      maxLatency: getIspMetricNumber(metric, "maxLatency"),
      packetLoss: getIspMetricNumber(metric, "packetLoss"),
    };
  });

  const dataRows = rows.filter((row) => (
    row.avgDownload !== null
    || row.avgUpload !== null
    || row.avgLatency !== null
    || row.packetLoss !== null
  ));
  const maxDownload = getMetricSeriesExtreme(dataRows, "avgDownload", "max");
  const maxUpload = getMetricSeriesExtreme(dataRows, "avgUpload", "max");
  const minLatency = getMetricSeriesExtreme(dataRows, "avgLatency", "min");

  return dataRows.map((row) => ({
    ...row,
    maxDownload,
    maxUpload,
    minLatency,
  }));
};

export const buildUbiquitiInternetSeries = (site, activeWan = "all-wans", ispMetrics = null, hostIds = [], selectedSourceId = "") => {
  const ispRows = buildUbiquitiIspInternetSeries(ispMetrics, getUbiquitiSiteId(site), hostIds, selectedSourceId);

  if (ispRows.length > 0) {
    return ispRows;
  }

  return activeWan ? [] : [];
};

const getUbiquitiClientFrequencyBand = (client) => {
  const rawBand = pickUbiquitiValue(client, [
    "frequency",
    "frequencyBand",
    "wifiBand",
    "radio",
    "radioBand",
    "band",
    "latestConnection.frequency",
    "latestConnection.frequencyBand",
    "latestConnection.radio",
    "latestConnection.radioBand",
  ]);
  const band = String(rawBand || "").toLowerCase();
  const frequency = Number(String(rawBand || "").replace(",", ".").match(/\d+(?:\.\d+)?/)?.[0]);

  if (Number.isFinite(frequency) && frequency >= 2400) {
    if (frequency >= 5925) {
      return "6";
    }

    if (frequency >= 4900) {
      return "5";
    }

    if (frequency >= 2400 && frequency < 2500) {
      return "2.4";
    }
  }

  if (band === "all" || band.includes("allradios")) {
    return "all";
  }

  if (band === "2" || band.includes("2.4") || band.includes("2_4") || band.includes("2g") || band.includes("ng")) {
    return "2.4";
  }

  if (band === "6" || band.includes("6ghz") || band.includes("6 ghz") || band.includes("6g") || band.includes("6e") || band.includes("ax6")) {
    return "6";
  }

  if (band === "5" || band.includes("5ghz") || band.includes("5 ghz") || band.includes("5g") || band.includes("ac") || band.includes("na")) {
    return "5";
  }

  return "";
};

const getWifiClientCountForFrequency = (clients, activeFrequency) => {
  const wifiClients = clients.filter((client) => client.isWifi);

  if (!wifiClients.length) {
    return {
      count: 0,
      isFrequencySpecific: false,
    };
  }

  const clientsWithBand = wifiClients
    .map((client) => ({ ...client, frequencyBand: getUbiquitiClientFrequencyBand(client) }))
    .filter((client) => client.frequencyBand);
  const matchingClients = clientsWithBand.filter((client) => client.frequencyBand === activeFrequency);

  if (clientsWithBand.length) {
    return {
      count: matchingClients.length,
      isFrequencySpecific: true,
    };
  }

  return {
    count: wifiClients.length,
    isFrequencySpecific: false,
  };
};

const flattenUbiquitiObjects = (value, depth = 0) => {
  if (!value || depth > 4) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenUbiquitiObjects(item, depth + 1));
  }

  if (typeof value !== "object") {
    return [];
  }

  return [
    value,
    ...Object.values(value)
      .filter((item) => item && typeof item === "object")
      .flatMap((item) => flattenUbiquitiObjects(item, depth + 1)),
  ];
};

const getWifiActivityValue = (activity, keys) => {
  for (const source of flattenUbiquitiObjects(activity)) {
    const value = pickUbiquitiValue(source, keys);
    const numericValue = toMetricNumber(value);

    if (numericValue !== null) {
      return numericValue;
    }
  }

  return null;
};

const getWifiActivityDisplayValue = (activity, keys) => {
  for (const source of flattenUbiquitiObjects(activity)) {
    const value = pickUbiquitiValue(source, keys);

    if (value !== null && value !== undefined && value !== "") {
      return String(value);
    }
  }

  return null;
};

const getWifiActivityRows = (wifiActivity, activeFrequency) => {
  const payload = wifiActivity?.data && typeof wifiActivity.data === "object" && !Array.isArray(wifiActivity.data)
    ? wifiActivity.data
    : wifiActivity;
  const rows = [
    ...(Array.isArray(payload?.points) ? payload.points : []),
    ...(Array.isArray(payload?.history) ? payload.history : []),
    ...getUbiquitiDataArray(payload),
  ];
  const requestedFrequency = activeFrequency === "all" ? "" : activeFrequency;

  return rows.filter((activity) => {
    const timestamp = normalizeMetricTimestamp(getMetricTimestamp(activity));
    const band = getUbiquitiClientFrequencyBand(activity);
    const hasWifiValues = getWifiActivityValue(activity, [
      "avgDownloadMbps",
      "avgUploadMbps",
      "avgTxRetriesPct",
      "clients",
      "wifiClients",
      "clientCount",
      "connectedClients",
      "txRetry",
      "txRetries",
      "connectivity",
      "success",
      "successPercent",
    ]) !== null;

    return timestamp && hasWifiValues && (!requestedFrequency || !band || band === requestedFrequency || band === "all");
  });
};

const buildUbiquitiWifiActivitySeries = (wifiActivity, activeFrequency) => {
  const activityRows = getWifiActivityRows(wifiActivity, activeFrequency)
    .sort((first, second) => {
      const firstTime = normalizeMetricTimestamp(getMetricTimestamp(first))?.getTime() || 0;
      const secondTime = normalizeMetricTimestamp(getMetricTimestamp(second))?.getTime() || 0;

      return firstTime - secondTime;
    });
  const visibleRows = activityRows;
  const labelIndexes = getUbiquitiAxisLabelIndexes(visibleRows.length);

  return visibleRows.map((activity, index) => {
    const timestamp = getMetricTimestamp(activity);
    const isLastPoint = index === visibleRows.length - 1;
    const isTickPoint = labelIndexes.has(index);
    const clients = getWifiActivityValue(activity, ["clients", "wifiClients", "clientCount", "connectedClients", "activeClients"]);
    const retries = getWifiActivityValue(activity, ["avgTxRetriesPct", "txRetry", "txRetries", "txRetryRate", "retryRate", "retries"]);
    const connectivity = getWifiActivityValue(activity, ["connectivity", "successPercent", "success", "successRate", "availability", "wifiConnectivity.successPercent"]);
    const avgDownload = getWifiActivityValue(activity, ["avgDownloadMbps", "downloadMbps", "download", "downloadRate"]);
    const avgUpload = getWifiActivityValue(activity, ["avgUploadMbps", "uploadMbps", "upload", "uploadRate"]);

    return {
      label: isLastPoint ? "Now" : isTickPoint ? formatMetricAxisLabel(timestamp) : "",
      tooltipLabel: isLastPoint ? `${formatMetricTooltipLabel(timestamp)} - Now` : formatMetricTooltipLabel(timestamp),
      avgDownload,
      avgDownloadDisplay: getWifiActivityDisplayValue(activity, ["avgDownloadDisplay", "downloadDisplay"]),
      avgUpload,
      avgUploadDisplay: getWifiActivityDisplayValue(activity, ["avgUploadDisplay", "uploadDisplay"]),
      clients,
      clientsDisplay: getWifiActivityDisplayValue(activity, ["wifiClientsDisplay", "clientsDisplay"]),
      retries,
      retriesDisplay: getWifiActivityDisplayValue(activity, ["avgTxRetriesDisplay", "txRetriesDisplay"]),
      connectivity,
      usesWifiActivity: true,
    };
  });
};

export const buildUbiquitiWifiSeries = (site, activeFrequency, clients = [], wifiActivity = null) => {
  const activityRows = buildUbiquitiWifiActivitySeries(wifiActivity, activeFrequency);

  if (activityRows.length > 0) {
    return activityRows;
  }

  const counts = site?.statistics?.counts || {};
  const retry = toMetricNumber(site?.statistics?.percentages?.txRetry);
  const apiWifiClients = getWifiClientCountForFrequency(clients, activeFrequency);
  const siteWifiClients = toMetricNumber(counts.wifiClient);
  const wifiClients = clients.length ? apiWifiClients.count : siteWifiClients;
  const hasLiveWifiData = clients.length || siteWifiClients !== null || retry !== null;

  if (!hasLiveWifiData) {
    return [];
  }

  return [{
    label: "Now",
    clients: wifiClients,
    retries: retry,
    connectivity: null,
    usesLiveWifiSnapshot: true,
  }];
};

export const getUbiquitiFrequencyProfile = (frequency) => {
  const profiles = {
    all: {
      label: "All Radios",
      density: ["is-soft", "is-ok", "is-ok", "is-ok"],
    },
    "2.4": {
      label: "2.4 GHz",
      density: ["is-warning", "is-soft", "is-ok", "is-ok"],
    },
    "5": {
      label: "5 GHz",
      density: ["is-soft", "is-ok", "is-ok", "is-ok"],
    },
    "6": {
      label: "6 GHz",
      density: ["is-ok", "is-ok", "is-soft", "is-soft"],
    },
  };

  return profiles[frequency] || profiles["5"];
};

export const getUbiquitiWanRows = (site) => {
  const wans = site?.statistics?.wans || {};

  return Object.entries(wans).map(([name, wan]) => {
    const uptime = Number(wan?.wanUptime);

    return {
      name,
      externalIp: wan?.externalIp || "-",
      uptime: Number.isFinite(uptime) ? Math.round(uptime) : null,
      issues: Array.isArray(wan?.wanIssues) ? wan.wanIssues.length : 0,
      isp: wan?.ispInfo?.name || site?.statistics?.ispInfo?.name || "-",
    };
  });
};

export const getUbiquitiHealthScore = (site, devices) => {
  const counts = site?.statistics?.counts || {};
  const total = counts.totalDevice || devices.length || 0;
  const offline = counts.offlineDevice || devices.filter((device) => device.status !== "online").length;
  const retry = site?.statistics?.percentages?.txRetry || 0;

  if (!total) {
    return 0;
  }

  return Math.max(0, Math.round(100 - (offline / total) * 65 - retry * 0.8));
};
